'use client'

import { smartSRPMessages } from '@/src/utils/srp/aiRecommendation/locale'
import { useLocale } from 'next-intl'
import { useEffect, useMemo, useRef } from 'react'

const STORAGE_KEY = 'hasSeenSmartSearchTour'
const POLL_INTERVAL_MS = 500
const MAX_POLL_ATTEMPTS = 20
const POST_ELEMENT_DELAY_MS = 800

export function useSmartSearchTour () {
    const locale = useLocale() as 'en' | 'ar'
    const tourMessages = useMemo(() => smartSRPMessages[locale].tour, [locale])
    const destroyRef = useRef<(() => void) | null>(null)

    useEffect(() => {
        if (typeof window === 'undefined') return

        try {
            if (localStorage.getItem(STORAGE_KEY)) return
        } catch {
            return
        }

        let cancelled = false
        let pollCount = 0

        const poll = setInterval(async () => {
            pollCount++
            if (cancelled || pollCount > MAX_POLL_ATTEMPTS) {
                clearInterval(poll)
                return
            }

            const target = document.getElementById('ai-search-sidebar')
            if (!target) return

            clearInterval(poll)

            await new Promise(r => setTimeout(r, POST_ELEMENT_DELAY_MS))
            if (cancelled) return

            const { driver } = await import('driver.js')
            // @ts-ignore CSS module import
            await import('driver.js/dist/driver.css')

            if (cancelled) return

            window.scrollTo({ top: 0, behavior: 'smooth' })
            await new Promise(r => setTimeout(r, 400))
            if (cancelled) return

            const driverObj = driver({
                showProgress: false,
                showButtons: ['close'],
                smoothScroll: false,
                overlayColor: 'rgba(0, 0, 0, 0.5)',
                popoverClass: 'smart-search-tour-popover',
                onDestroyStarted: () => {
                    try {
                        localStorage.setItem(STORAGE_KEY, 'true')
                    } catch { /* privacy mode */ }
                    driverObj.destroy()
                },
                steps: [{
                    element: '#ai-search-sidebar',
                    popover: {
                        title: tourMessages.searchBox.title,
                        description: tourMessages.searchBox.description,
                        side: 'right',
                        align: 'start'
                    }
                }]
            })

            destroyRef.current = () => driverObj.destroy()
            driverObj.drive()
        }, POLL_INTERVAL_MS)

        return () => {
            cancelled = true
            clearInterval(poll)
            destroyRef.current?.()
            destroyRef.current = null
        }
    }, [tourMessages])
}
